/**
 * Landing Process organism component
 * Showcases the 5-step process for using the Furniturr application
 */

// #region Imports
import {
    Box,
    Typography,
    Container,
    useTheme,
    Stack,
    Paper,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    useMediaQuery
} from "@mui/material";
import {
    Build,
    Layers,
    Category,
    PlayArrow,
    Download
} from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
// #endregion

// #region Types
/**
 * Process step interface
 */
interface ProcessStep {
    id: string;
    label: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    features: string[];
}

/**
 * Props for LandingProcess organism component
 */
interface LandingProcessOrganismProps {
    /** Optional test ID for testing purposes */
    "data-testid"?: string;
}
// #endregion

// #region Data
/**
 * Process step IDs and icons configuration
 */
const PROCESS_STEP_CONFIG = [
    { id: "cutterConfig", icon: <Build /> },
    { id: "textureManagement", icon: <Layers /> },
    { id: "pieceManagement", icon: <Category /> },
    { id: "layoutGeneration", icon: <PlayArrow /> },
    { id: "resultsDownload", icon: <Download /> },
];
// #endregion

// #region Component
/**
 * LandingProcess organism component showcasing the application workflow
 * Displays a visual stepper with detailed process steps
 *
 * @param props - The component props
 * @returns JSX element representing the process section
 */
function LandingProcessOrganism({ ...props }: LandingProcessOrganismProps) {
    const theme = useTheme();
    const { t } = useTranslation('landing');
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Generate process steps with translations
    const processSteps: ProcessStep[] = PROCESS_STEP_CONFIG.map((config, index) => ({
        id: config.id,
        label: t(`process.steps.${config.id}.label`),
        title: t(`process.steps.${config.id}.title`),
        description: t(`process.steps.${config.id}.description`),
        icon: config.icon,
        features: t(`process.steps.${config.id}.features`, { returnObjects: true }) as string[],
    }));

    return (
        <Box
            component="section"
            aria-labelledby="process-heading"
            sx={{
                py: { xs: 8, md: 12 },
                backgroundColor: "background.default",
                position: "relative",
            }}
            {...props}
        >
            <Container maxWidth="lg">
                {/* Section Header */}
                <Box sx={{ textAlign: "center", mb: 8 }}>
                    <Typography
                        variant="overline"
                        sx={[
                            {
                                color: theme.palette.text.secondary,
                                fontWeight: 600,
                                letterSpacing: 1,
                                mb: 2,
                                display: "block",
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.6)",
                                }),
                        ]}
                    >
                        {t('process.sectionTitle')}
                    </Typography>
                    <Typography
                        variant="h2"
                        component="h2"
                        id="process-heading"
                        sx={{
                            mb: 3,
                            fontSize: { xs: "2rem", md: "3rem" },
                            fontWeight: 700,
                        }}
                    >
                        {t('process.title.main')}{" "}
                        <Box component="span" sx={{ color: theme.palette.secondary.main }}>
                            {t('process.title.accent')}
                        </Box>
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={[
                            {
                                color: theme.palette.text.secondary,
                                maxWidth: 600,
                                mx: "auto",
                                lineHeight: 1.6,
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.8)",
                                }),
                        ]}
                    >
                        {t('process.subtitle')}
                    </Typography>
                </Box>

                {/* Process Steps */}
                {isMobile ? (
                    // Mobile: Vertical Cards
                    <Stack spacing={4} sx={{ alignItems: "center" }}>
                        {processSteps.map((step: ProcessStep, index: number) => (
                            <Paper
                                key={step.id}
                                elevation={2}
                                sx={{
                                    p: 4,
                                    position: "relative",
                                    overflow: "hidden",
                                    maxWidth: 800,
                                    width: "100%",
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: 4,
                                        height: "100%",
                                        background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                    },
                                }}
                            >
                                <Stack direction="row" spacing={3} alignItems="flex-start">
                                    {/* Step Number & Icon */}
                                    <Box sx={{ textAlign: "center", minWidth: 60 }}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: "50%",
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.primary.contrastText,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                mb: 1,
                                                "& .MuiSvgIcon-root": {
                                                    fontSize: "1.5rem",
                                                },
                                            }}
                                        >
                                            {step.icon}
                                        </Box>
                                        <Typography
                                            variant="caption"
                                            sx={[
                                                {
                                                    color: theme.palette.text.secondary,
                                                    fontWeight: 600,
                                                    fontSize: "0.75rem",
                                                },
                                                (theme) =>
                                                    theme.applyStyles("dark", {
                                                        color: "rgba(255, 255, 255, 0.7)",
                                                    }),
                                            ]}
                                        >
                                            {step.label}
                                        </Typography>
                                    </Box>

                                    {/* Content */}
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            component="h3"
                                            sx={[
                                                {
                                                    mb: 2,
                                                    fontWeight: 600,
                                                    color: theme.palette.text.primary,
                                                },
                                                (theme) =>
                                                    theme.applyStyles("dark", {
                                                        color: "#FFFFFF",
                                                    }),
                                            ]}
                                        >
                                            {step.title}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={[
                                                {
                                                    mb: 3,
                                                    color: theme.palette.text.secondary,
                                                    lineHeight: 1.6,
                                                },
                                                (theme) =>
                                                    theme.applyStyles("dark", {
                                                        color: "rgba(255, 255, 255, 0.8)",
                                                    }),
                                            ]}
                                        >
                                            {step.description}
                                        </Typography>
                                        <Stack spacing={1}>
                                            {step.features.map((feature: string, featureIndex: number) => (
                                                <Typography
                                                    key={featureIndex}
                                                    variant="body2"
                                                    sx={[
                                                        {
                                                            color: theme.palette.text.secondary,
                                                            "&::before": {
                                                                content: '"•"',
                                                                color: theme.palette.primary.main,
                                                                fontWeight: "bold",
                                                                marginRight: 1,
                                                            },
                                                        },
                                                        (theme) =>
                                                            theme.applyStyles("dark", {
                                                                color: "rgba(255, 255, 255, 0.8)",
                                                            }),
                                                    ]}
                                                >
                                                    {feature}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                ) : (
                    // Desktop: Horizontal Stepper
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Stepper
                            activeStep={processSteps.length} // Show all steps as completed with content visible
                            orientation="vertical"
                            sx={{
                                maxWidth: 800,
                                "& .MuiStepConnector-root": {
                                    display: "none", // Hide connecting lines
                                },
                            }}
                        >
                        {processSteps.map((step: ProcessStep, index: number) => (
                            <Step key={step.id} expanded>
                                <StepLabel
                                    StepIconComponent={() => (
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: "50%",
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.primary.contrastText,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                "& .MuiSvgIcon-root": {
                                                    fontSize: "1.5rem",
                                                },
                                            }}
                                        >
                                            {step.icon}
                                        </Box>
                                    )}
                                    sx={{
                                        "& .MuiStepLabel-labelContainer": {
                                            ml: 3,
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="overline"
                                        sx={[
                                            {
                                                color: theme.palette.text.secondary,
                                                fontWeight: 600,
                                                fontSize: "0.75rem",
                                                display: "block",
                                            },
                                            (theme) =>
                                                theme.applyStyles("dark", {
                                                    color: "rgba(255, 255, 255, 0.7)",
                                                }),
                                        ]}
                                    >
                                        {step.label}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        component="h3"
                                        sx={[
                                            {
                                                fontWeight: 600,
                                                color: theme.palette.text.primary,
                                                mb: 1,
                                            },
                                            (theme) =>
                                                theme.applyStyles("dark", {
                                                    color: "#FFFFFF",
                                                }),
                                        ]}
                                    >
                                        {step.title}
                                    </Typography>
                                </StepLabel>
                                <StepContent
                                    sx={{
                                        ml: 9, // Align with step title text
                                        pb: 4,
                                        borderLeft: "none",
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={[
                                            {
                                                mb: 3,
                                                color: theme.palette.text.secondary,
                                                lineHeight: 1.6,
                                                maxWidth: 500,
                                            },
                                            (theme) =>
                                                theme.applyStyles("dark", {
                                                    color: "rgba(255, 255, 255, 0.8)",
                                                }),
                                        ]}
                                    >
                                        {step.description}
                                    </Typography>
                                    <Stack spacing={1}>
                                        {step.features.map((feature: string, featureIndex: number) => (
                                            <Typography
                                                key={featureIndex}
                                                variant="body2"
                                                sx={[
                                                    {
                                                        color: theme.palette.text.secondary,
                                                        "&::before": {
                                                            content: '"•"',
                                                            color: theme.palette.primary.main,
                                                            fontWeight: "bold",
                                                            marginRight: 1,
                                                        },
                                                    },
                                                    (theme) =>
                                                        theme.applyStyles("dark", {
                                                            color: "rgba(255, 255, 255, 0.8)",
                                                        }),
                                                ]}
                                            >
                                                {feature}
                                            </Typography>
                                        ))}
                                    </Stack>
                                </StepContent>
                            </Step>
                        ))}
                        </Stepper>
                    </Box>
                )}
            </Container>
        </Box>
    );
}
// #endregion

// #region Exports
export default LandingProcessOrganism;
export type { LandingProcessOrganismProps };
// #endregion
